import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { Formik, Form } from "formik";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useAuthStore } from "../hooks";
import { ChangePasswordSchema } from "../schemas";
import { PATHS } from "@/lib/router";
import { checkGroupFromRole } from "@/helpers";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/common";
import { getFirstAllowedRoute } from "@/app/routes/public";
import { AuthStandaloneShell } from "../components";

const ChangePasswordPage = observer(() => {
  const navigate = useNavigate();
  const { t } = useTran();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { changePasswordForm, loading, changePassword, user } = useAuthStore();
  const isNewUser = user?.isNew === true;

  const getPostChangeRedirect = () => {
    const redirectRoute = getFirstAllowedRoute(user?.permissions);
    const group = checkGroupFromRole(user?.role);
    if (group === "admin_group") return PATHS.USER_LIST;
    if (group === "bank_group") return PATHS.CARBON_DASHBOARD;
    return redirectRoute;
  };

  const handleSubmit = async (
    values,
    { setErrors, setSubmitting, setTouched },
  ) => {
    const success = await changePassword(values);
    setSubmitting(false);

    if (success) {
      navigate(getPostChangeRedirect(), { replace: true });
      return;
    }

    if (
      changePasswordForm?.errors &&
      Object.keys(changePasswordForm.errors).length > 0
    ) {
      setErrors({ ...changePasswordForm.errors });
      setTouched(
        { currentPassword: true, newPassword: true, confirmPassword: true },
        true,
      );
    }
  };

  return (
    <AuthStandaloneShell>
      <Formik
        initialValues={{
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }}
        validationSchema={ChangePasswordSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, values, handleChange }) => (
          <Form className="flex flex-col">
            <div className="mb-6 flex items-center justify-center gap-2 text-center">
              <Lock className="h-7 w-7 text-primary sm:h-8 sm:w-8" />
              <h1 className="text-2xl font-semibold text-primary sm:text-3xl">
                {t(
                  isNewUser
                    ? "auth.changePassword.setTitle"
                    : "auth.changePassword.title",
                )}
              </h1>
            </div>

            <p className="mb-6 text-center text-sm text-muted-foreground">
              {t(
                isNewUser
                  ? "auth.changePassword.setDescription"
                  : "auth.changePassword.description",
              )}
            </p>

            <FormField
              name="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              placeholder={t("auth.changePassword.currentPassword")}
              value={values.currentPassword}
              onChange={handleChange}
              error={touched.currentPassword && errors.currentPassword}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword((state) => !state)}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              }
              className="mb-4"
            />

            <FormField
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              placeholder={t("auth.changePassword.newPassword")}
              value={values.newPassword}
              onChange={handleChange}
              error={touched.newPassword && errors.newPassword}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowNewPassword((state) => !state)}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showNewPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              }
              className="mb-4"
            />

            <FormField
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t("auth.changePassword.confirmPassword")}
              value={values.confirmPassword}
              onChange={handleChange}
              error={touched.confirmPassword && errors.confirmPassword}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((state) => !state)}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              }
              className="mb-4"
            />

            {changePasswordForm?.serverError && (
              <p className="mb-4 text-center text-sm text-red-600">
                {changePasswordForm.serverError}
              </p>
            )}

            <Button
              type="submit"
              disabled={isSubmitting || loading}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              {isSubmitting || loading
                ? t("auth.changePassword.changing")
                : t("auth.changePassword.submit")}
            </Button>

            {!isNewUser && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(PATHS.DASHBOARD)}
                className="mt-4 w-full"
              >
                {t("auth.changePassword.cancel")}
              </Button>
            )}
          </Form>
        )}
      </Formik>
    </AuthStandaloneShell>
  );
});

export default ChangePasswordPage;
